import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, BRAND } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, MapPin, Send, CheckCircle2, ArrowLeft, MessageSquare } from "lucide-react";

const CONTACT_EMAIL = "contact@drthrive365.com";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent. We'll get back to you soon.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-40 glass">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-8 h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="contact-brand">
            <img src={BRAND.logo} alt="Dr Thrive 365" className="h-9 w-9 rounded-md object-cover" />
            <span className="font-display text-xl">Dr Thrive 365</span>
          </Link>
          <Link
            to="/"
            className="text-sm text-white/60 hover:text-white flex items-center gap-2"
            data-testid="contact-back-home"
          >
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </nav>

      {/* Ambient gradients */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 left-1/3 w-[500px] h-[500px] rounded-full bg-[#FF8A00]/10 blur-[160px]" />
        <div className="absolute bottom-0 -right-40 w-[420px] h-[420px] rounded-full bg-[#E52E71]/10 blur-[140px]" />
      </div>

      <section
        data-testid="contact-page"
        className="relative pt-32 pb-24 px-4 sm:px-8"
      >
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-14">
          {/* Left — copy + contact info */}
          <div>
            <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#FF8A00] mb-4">
              <MessageSquare size={14} /> Contact
            </div>
            <h1 className="font-display text-5xl sm:text-6xl leading-[1.02]">
              Let&apos;s talk about<br />
              <span className="text-brand-gradient">your protocol.</span>
            </h1>
            <p className="text-white/60 mt-6 leading-relaxed max-w-md">
              Questions about a program, a booking, or a partnership? Send a note and
              we&apos;ll get back within 1–2 business days.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="contact-email-link"
                className="glass rounded-2xl p-5 flex items-center gap-4 hover:-translate-y-0.5 transition-transform border border-white/8"
              >
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#FF8A00]/25 to-[#E52E71]/25 flex items-center justify-center">
                  <Mail size={18} className="text-[#FF8A00]" />
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-white/40">Email us directly</div>
                  <div className="font-display text-lg text-white mt-0.5">{CONTACT_EMAIL}</div>
                </div>
              </a>

              <div className="glass rounded-2xl p-5 flex items-center gap-4 border border-white/8">
                <div className="w-11 h-11 rounded-full bg-white/5 flex items-center justify-center">
                  <MapPin size={18} className="text-white/60" />
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-white/40">Response window</div>
                  <div className="text-white/85 mt-0.5">Mon – Fri &nbsp;·&nbsp; typically within 24 hours</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div>
            <div className="glass rounded-2xl p-6 sm:p-8 border border-white/10">
              {done ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-[#FF8A00] to-[#E52E71] flex items-center justify-center">
                    <CheckCircle2 size={22} />
                  </div>
                  <div className="font-display text-2xl mt-5">Message sent.</div>
                  <p className="text-sm text-white/60 mt-2 max-w-xs mx-auto">
                    Thanks — we&apos;ll reply to <span className="text-white/85">{form.email}</span> within
                    1–2 business days.
                  </p>
                  <Button
                    onClick={() => { setDone(false); setForm({ name:"",email:"",phone:"",subject:"",message:"" }); }}
                    variant="outline"
                    data-testid="contact-send-another"
                    className="mt-6 rounded-full bg-transparent border-white/15 hover:bg-white/5"
                  >
                    Send another
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[11px] tracking-[0.2em] uppercase text-white/50">Name</label>
                      <Input
                        data-testid="contact-name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Jane Doe"
                        className="mt-1.5 bg-black/40 border-white/10 focus:border-[#FF8A00] h-11"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] tracking-[0.2em] uppercase text-white/50">Email</label>
                      <Input
                        data-testid="contact-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="you@domain.com"
                        className="mt-1.5 bg-black/40 border-white/10 focus:border-[#FF8A00] h-11"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[11px] tracking-[0.2em] uppercase text-white/50">
                        Phone <span className="text-white/25 normal-case tracking-normal">(optional)</span>
                      </label>
                      <Input
                        data-testid="contact-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+1 555 123 4567"
                        className="mt-1.5 bg-black/40 border-white/10 focus:border-[#FF8A00] h-11"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] tracking-[0.2em] uppercase text-white/50">
                        Subject <span className="text-white/25 normal-case tracking-normal">(optional)</span>
                      </label>
                      <Input
                        data-testid="contact-subject"
                        value={form.subject}
                        onChange={(e) => setForm({ ...form, subject: e.target.value })}
                        placeholder="e.g. Cohort inquiry"
                        className="mt-1.5 bg-black/40 border-white/10 focus:border-[#FF8A00] h-11"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] tracking-[0.2em] uppercase text-white/50">Message</label>
                    <Textarea
                      data-testid="contact-message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="What's on your mind?"
                      rows={6}
                      className="mt-1.5 bg-black/40 border-white/10 focus:border-[#FF8A00] resize-none"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="w-full rounded-full h-12 mt-2 bg-gradient-to-r from-[#FF8A00] to-[#E52E71] hover:opacity-90 text-white border-0 disabled:opacity-60"
                  >
                    {loading ? "Sending..." : (<>Send message <Send size={16} className="ml-2" /></>)}
                  </Button>
                  <p className="text-[11px] text-white/35 text-center leading-relaxed">
                    We&apos;ll use your details only to reply to you. No marketing lists unless you opt in.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
